/**
 * 관제 서버 조회 호출 (7회차 결정 2·3·4).
 *
 * SSE는 `store/stream.ts`가 따로 다룬다. 여기는 한 번 받고 끝나는 조회뿐이다.
 * 개발 서버가 `/api`를 관제 서버로 프록시하므로 주소에 포트를 쓰지 않는다(CLAUDE.md 포트 규칙).
 */

/** 서버가 준 실패 메시지를 그대로 화면에 보이기 위한 오류 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * 관제 서버에 닿지 못했을 때의 문구 (44회차). 43회차까지는 브라우저의 영문 `Failed to fetch`가
 * 모든 구획에 그대로 떴다. 무엇이 잘못됐고 무엇을 볼지 적는다
 */
export const UNREACHABLE =
  '관제 서버에 닿지 못했다. 관제 서버가 떠 있는지, 이 PC 에서 그 주소로 가는 네트워크가 막히지 않았는지 확인할 것';

/**
 * 실패한 응답의 문구 (44회차). 서버가 준 한글 문구가 있으면 그것을 그대로 쓴다.
 * 문구 없는 5xx 는 대개 관제 서버 앞의 프록시(nginx 502·504, 개발 서버 500)가 낸 것이라 그것을 적는다
 */
export async function failureMessage(
  response: Response,
  fallback = '조회에 실패했다',
): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string' && body.message !== '') return body.message;
  } catch {
    // 본문이 JSON이 아니면 상태 코드로 알린다
  }
  if (response.status >= 500) {
    return (
      `${fallback} (${response.status}). 관제 서버가 응답하지 못했다. ` +
      '관제 서버가 떠 있는지, 화면과 관제 서버 사이의 프록시 설정을 확인할 것'
    );
  }
  return `${fallback} (${response.status})`;
}

/**
 * 계약과 다른 응답의 문구 (44회차). 43회차까지는 zod 오류 JSON 이 여러 줄로 그대로 떴다.
 * 할 일(판 맞추기)을 먼저 적고, 어긋난 칸 경로를 셋까지 붙인다
 */
export function contractMessage(error: Error): string {
  const issues = (error as { issues?: { path?: readonly PropertyKey[] }[] }).issues;
  const paths = Array.isArray(issues)
    ? issues.slice(0, 3).map((issue) => {
        const path = (issue.path ?? []).map(String).join('.');
        return path === '' ? '(응답 전체)' : path;
      })
    : [];
  const more = Array.isArray(issues) && issues.length > 3 ? ` 외 ${issues.length - 3}곳` : '';
  return (
    '서버 응답이 계약과 다르다. 관제 화면과 관제 서버를 같은 판으로 맞추고 새로고침할 것' +
    (paths.length > 0 ? ` (어긋난 칸: ${paths.join(', ')}${more})` : '')
  );
}

/**
 * 로그인 세션이 걸린 주소인지 (51회차). 서버의 `http/session-auth.ts` `routeOf`와 **같은 판정**이다.
 *
 * 이 주소의 401만 "세션이 풀렸다"로 읽는다. 로그인·로그아웃 자체의 401(비밀번호 틀림)과
 * 외부 연동 Bearer 문의 401(토큰 없음)은 세션과 무관하다. 토큰 관리(`/api/external/tokens`)는
 * 세션 안이다.
 *
 * 서버처럼 `/api` 밑인지와 토큰 관리인지는 소문자로 보고, 문 밖·Bearer 예외는 글자까지 같을 때만
 * 준다. 절대 주소는 경로만 본다 (리뷰 R51-B). 화면이 만드는 주소는 늘 소문자 상대 경로라 지금 갈리는
 * 곳은 없지만, 두 판정이 어긋나면 서버가 세션 401을 준 요청을 화면이 흘려 넘기게 된다
 */
export function isSessionPath(url: string): boolean {
  const rest = url.replace(/^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/?#]*/, '');
  const path = rest.split(/[?#]/, 1)[0] || '/';
  const lower = path.toLowerCase();
  if (lower !== '/api' && !lower.startsWith('/api/')) return false;
  if (path === '/api/auth/login' || path === '/api/auth/logout') return false;
  const tokenAdmin = lower === '/api/external/tokens' || lower.startsWith('/api/external/tokens/');
  return tokenAdmin || !path.startsWith('/api/external/');
}

/** 세션 401을 받았을 때 부를 곳. `auth/useSession.ts`가 모듈을 읽을 때 건다 */
let unauthorized: (() => void) | undefined;

/**
 * 401을 한곳에서 받는 자리 (51회차). 조회 훅·편집 화면·실시간 스토어가 모두 `request`를 거치므로
 * 여기 하나로 로그인 화면으로 돌아간다. `api.ts`가 세션 모듈을 import하지 않도록 거꾸로 꽂는다
 */
export function setUnauthorizedHandler(handler: (() => void) | undefined): void {
  unauthorized = handler;
}

/** `fetch` 한 번. 닿지 못하면 한글 문구의 `ApiError`로 올린다. 취소는 그대로 올린다 (44회차) */
export async function request(url: string, init?: RequestInit): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    // 취소는 부르는 쪽이 신호로 가른다. 바꾸지 않는다
    if (init?.signal?.aborted === true) throw err;
    throw new ApiError(0, UNREACHABLE);
  }
  // 세션이 풀렸다. 부른 쪽은 지금처럼 실패를 받고, 화면은 로그인으로 돌아간다 (51회차)
  if (response.status === 401 && isSessionPath(url)) unauthorized?.();
  return response;
}

/**
 * 성공한 응답의 본문을 읽는다 (44회차). JSON 이 아니면(프록시가 HTML 을 돌려주는 등) 또는 계약과
 * 다르면 한글 문구의 `ApiError`로 올린다. 상태 코드는 응답 그대로 둔다
 */
export async function readJson<T>(response: Response, schema?: ResponseSchema<T>): Promise<T> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError(
      response.status,
      `서버 응답을 JSON 으로 읽지 못했다 (${response.status}). ` +
        '화면과 관제 서버 사이의 프록시가 다른 페이지를 돌려주는지 확인할 것',
    );
  }
  if (schema === undefined) return body as T;
  const parsed = schema.safeParse(body);
  if (!parsed.success) throw new ApiError(response.status, contractMessage(parsed.error));
  return parsed.data;
}

/**
 * 계약 스키마가 보이는 모양만 적어 둔다 (17회차).
 *
 * 콘솔은 `zod`를 직접 의존하지 않는다(`@apm/contracts`를 소스로 소비할 뿐이다).
 * `safeParse` 하나만 구조로 받으면 zod import 없이 계약 스키마를 그대로 넘길 수 있다.
 */
export interface ResponseSchema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false; error: Error };
}

/**
 * 조회 한 번. `schema`를 주면 받은 본문을 계약으로 검증한다 (17회차, 감사 C-8).
 *
 * 16회차까지는 `as T` 캐스팅뿐이라 서버가 필드를 빼도 화면이 `undefined`를 그리다 뒤늦게
 * 터졌다. 이제는 받은 자리에서 걸린다. 검증 실패는 `ApiError`로 올려 다른 실패와 같은 길로
 * 화면에 뜬다(상태 코드는 응답 그대로 둔다 — 통신은 성공했고 모양이 틀린 것이다).
 *
 * `schema`가 없으면 지금까지처럼 캐스팅만 한다. 남은 자리는 검색 화면의 인라인 조회 하나뿐이고
 * 그 화면은 19회차에 훅으로 옮기면서 검증에 들어간다.
 */
export async function getJson<T>(
  url: string,
  schema?: ResponseSchema<T>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await request(url, { signal });
  if (!response.ok) throw new ApiError(response.status, await failureMessage(response));
  return readJson(response, schema);
}

/** 값이 있는 것만 쿼리스트링으로 만든다 */
export function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}
